/**
 * Create the store with dynamic reducers
 */

import { configureStore as createToolkitStore, Tuple } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { reduxTimeout } from 'redux-timeout';
import { PRODUCTION } from './config';
import createReducer, { STATIC_REDUCER_KEYS } from './reducers';
import createActionLoggerMiddleware from './redux-middlewares/actionLogger';
import analyticsMiddleware from './redux-middlewares/analytics';
import { locationChanged, routerMiddleware } from './routing';
import { generateDeviceID } from './utils/device';
import { generatePisanoSession } from './utils/pisanoSession';

const createPreloadedStateReducers = (initialState, initialReducers) =>
  Object.entries(initialState).reduce((reducers, [key, value]) => {
    const hasInitialReducer = Object.prototype.hasOwnProperty.call(initialReducers, key);
    if (!STATIC_REDUCER_KEYS.includes(key) && !hasInitialReducer && value !== undefined) {
      // Code-split slices may be preloaded before their reducer mounts. A
      // pass-through reducer keeps that state alive until injection replaces it.
      reducers[key] = (state = value) => state;
    }
    return reducers;
  }, {});

export default function configureStore(initialState = {}, history, initialReducers = {}) {
  generateDeviceID();
  generatePisanoSession();

  // A saga middleware instance can only be bound to one Redux store. Keeping it
  // local makes application/widget stores and stores created by tests independent.
  const sagaMiddleware = createSagaMiddleware();

  window.PisanoActions = [];
  const middlewares = [
    createActionLoggerMiddleware(window.PisanoActions), // HACK: Don't use window to hold previous actions.
    analyticsMiddleware,
    sagaMiddleware,
  ];
  if (history) {
    middlewares.push(routerMiddleware(history));
  }
  middlewares.push(reduxTimeout());

  const injectedReducers = {
    ...createPreloadedStateReducers(initialState, initialReducers),
    ...initialReducers,
  };

  const store = createToolkitStore({
    reducer: createReducer(injectedReducers),
    preloadedState: initialState,
    // Sagas remain the async orchestration boundary. Keeping the established
    // middleware list avoids adding thunk or development checks that reject
    // legitimate File, DOM, and callback values carried by existing actions.
    middleware: () => new Tuple(...middlewares),
    // The app already coordinates React/Redux update timing. Do not introduce
    // RTK's opt-in auto-batching behavior during this compatibility migration.
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers({ autoBatch: false }),
    devTools: !PRODUCTION && { shouldHotReload: false },
  });

  // Extensions
  store.runSaga = sagaMiddleware.run;
  store.injectedReducers = injectedReducers; // Reducer registry
  store.injectedSagas = {}; // Saga registry

  if (history) {
    // Keep the existing route state contract without a legacy context bridge.
    store.dispatch(locationChanged(history.location));
  }

  // Make reducers hot reloadable, see http://mxs.is/googmo
  // Vitest evaluates this module as ESM, where Webpack's `module` global does not exist.
  if (typeof module !== 'undefined' && module.hot) {
    module.hot.accept('./reducers', () => {
      store.replaceReducer(createReducer(store.injectedReducers));
    });
  }

  return store;
}
